import { apiClient } from '../lib/apiClient';

export interface TeamActiveRep {
  userId: string;
  email: string;
  name: string | null;
  role: 'sales_rep' | 'manager' | 'organization_admin' | 'super_admin';
  otherStationCount: number;
}

export interface TeamPendingInvite {
  id: string;
  email: string;
  /** CTV-764: optional display name captured on the invite form. */
  name: string | null;
  /** CTV-764: the role this invite will mint on accept. */
  role: 'sales_rep' | 'manager';
  invitedByEmail: string | null;
  invitedAt: string;
  expiresAt: string;
}

/** CTV-764: one Team-tab invite entry — optional name, email, role. */
export interface TeamInviteRequest {
  email: string;
  role: 'sales_rep' | 'manager';
  name?: string;
}

/**
 * A team member the publisher listed on their onboarding form who hasn't
 * signed up yet. Distinct from a pending INVITE: no email has been sent — the
 * access is pre-authorized and applies itself on their first sign-in.
 */
export interface TeamPendingClaim {
  id: string;
  email: string;
  name: string | null;
  role: 'sales_rep' | 'manager';
  createdAt: string | null;
  expiresAt: string | null;
  /** e.g. EMAIL_BELONGS_TO_OTHER_ORG — needs a super admin to resolve. */
  conflictCode: string | null;
}

export interface TeamPageData {
  station: { id: string; slug: string; name: string };
  activeReps: TeamActiveRep[];
  pendingInvites: TeamPendingInvite[];
  pendingClaims: TeamPendingClaim[];
}

export type InviteOutcome =
  | 'invite_sent'
  | 'access_added'
  | 'already_has_access'
  // CTV-764: pending invite already exists — Resend or Revoke instead.
  | 'already_invited'
  | 'rejected_other_org'
  // CTV-673 audit: buyer-side login; can never become publisher staff.
  | 'rejected_advertiser_email'
  | 'rejected_invalid_email';

export interface InviteResult {
  email: string;
  status: InviteOutcome;
  inviteId?: string;
  targetUserId?: string;
  /** When set, the server has matched this email to an existing auth.users
   *  row. Drives role-aware copy in the result banner. */
  targetRole?: string;
  /** CTV-764: for `already_invited`, the role the pending invite carries. */
  pendingRole?: 'sales_rep' | 'manager';
  error?: string;
}

export interface InviteResultSummary {
  results: InviteResult[];
  counts: {
    sent: number;
    added: number;
    skipped: number;
    alreadyInvited: number;
    rejected: number;
  };
}

export interface StationInviteView {
  stationId: string;
  stationName: string;
  stationSlug: string | null;
  publisherName: string;
  email: string;
  /** CTV-764: the role this invite mints, shown on the landing page. */
  role: 'sales_rep' | 'manager';
  inviterEmail: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedAt: string;
  expiresAt: string;
}

export interface ApplyClaimsResult {
  applied: number;
  stations: string[];
  conflicts: Array<{ claimId: string; stationSlug: string | null; code: string }>;
  /** Authoritative access after the call, for comparing against the token. */
  access: { role: string | null; stations: string[] } | null;
}

export const stationTeamService = {
  /**
   * Settle any station access pre-authorized for the caller's email.
   *
   * Called right after sign-in. The claim mutates app_metadata server-side,
   * but the JWT in hand was minted before that, so a non-zero `applied` means
   * the caller must refresh their session to see the new role and stations.
   */
  applyClaims: () =>
    apiClient.post<ApplyClaimsResult>('/station-invites/claims/apply'),

  listTeam: (slug: string) =>
    apiClient.get<TeamPageData>(`/stations/${encodeURIComponent(slug)}/team`),

  inviteTeamMembers: (slug: string, invites: TeamInviteRequest[]) =>
    apiClient.post<InviteResultSummary>(
      `/stations/${encodeURIComponent(slug)}/team/invite`,
      { invites },
    ),

  resendInvite: (slug: string, inviteId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/invites/${encodeURIComponent(inviteId)}/resend`,
    ),

  revokeInvite: (slug: string, inviteId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/invites/${encodeURIComponent(inviteId)}/revoke`,
    ),

  removeRep: (slug: string, userId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/members/${encodeURIComponent(userId)}/remove`,
    ),

  lookupInvite: (token: string) =>
    apiClient.get<StationInviteView>(
      `/station-invites/${encodeURIComponent(token)}`,
      { anonymous: true },
    ),

  acceptInvite: (token: string) =>
    apiClient.post<
      | { ok: true; stationSlug: string; passwordRequired: boolean }
      | { ok: false; needsSignIn: true }
    >(`/station-invites/${encodeURIComponent(token)}/accept`, { token }),

  acceptInviteWithPassword: (token: string, password: string) =>
    apiClient.post<{ ok: true; email: string; stationSlug: string }>(
      `/station-invites/${encodeURIComponent(token)}/accept-with-password`,
      { token, password },
      { anonymous: true },
    ),
};
